import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Alert, CircularProgress, Skeleton, Checkbox, Tooltip,
  Chip, Select, MenuItem, FormControl, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Print, Refresh, ArrowBack, Download, ExpandMore,
  Visibility, Delete, Close, PrintOutlined
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/authApi';

const fmt2 = v => parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmt3 = v => parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

function generatePeriods(periodStr, monthsBack = 24) {
  const periods = [];
  const ranges  = (periodStr || '01-15, 16-ME')
    .split(',').map(s => s.trim()).filter(Boolean)
    .map(r => { const [s,e]=r.split('-').map(x=>x.trim()); return { start:parseInt(s), end:e==='ME'?'ME':parseInt(e) }; });
  const now=new Date(), curY=now.getFullYear(), curM=now.getMonth();
  const pad=d=>String(d).padStart(2,'0');
  for (let mi=0; mi<=monthsBack; mi++) {
    const d=new Date(curY,curM-mi,1), year=d.getFullYear(), month=d.getMonth();
    if (year<2020) break;
    const lastDay=new Date(year,month+1,0).getDate();
    const monthStr=String(month+1).padStart(2,'0');
    const monthName=d.toLocaleString('en-IN',{month:'short'});
    for (let ri=ranges.length-1; ri>=0; ri--) {
      const range=ranges[ri], startDay=range.start, endDay=range.end==='ME'?lastDay:Math.min(range.end,lastDay);
      const fromISO=`${year}-${monthStr}-${pad(startDay)}`, toISO=`${year}-${monthStr}-${pad(endDay)}`;
      if (new Date(year,month,startDay)>now) continue;
      const toMonthName=new Date(year,month,endDay).toLocaleString('en-IN',{month:'short'});
      periods.push({ label:`${pad(startDay)} ${monthName} ${year} - ${pad(endDay)} ${toMonthName} ${year}`, fromDate:fromISO, toDate:toISO });
    }
  }
  return periods;
}

// ─── Build farmer bill HTML ───────────────────────────
function buildFarmerBill(row, fromDate, toDate, centerName, centerCode, printerType = 'A4') {
  const is2inch = printerType === '2inch';
  const dateLabel = `${fromDate.split('-').reverse().join('/')} To ${toDate.split('-').reverse().join('/')}`;

  // ✅ All collections — each row printed individually (no merge by date)
  // This handles multiple entries on same date same shift correctly
  const mornings = row.morningCollections || [];
  const evenings  = row.eveningCollections  || [];

  // Flat list of all collections sorted by date then shift
  const allCols = [
    ...mornings.map(c => ({ ...c, shiftLabel: 'M' })),
    ...evenings.map(c => ({ ...c, shiftLabel: 'E' })),
  ].sort((a, b) => {
    const da = a.date || a.collectionDate || '';
    const db = b.date || b.collectionDate || '';
    if (da !== db) return da.localeCompare(db);
    return (a.shift || 1) - (b.shift || 1);
  });

  const totLit = allCols.reduce((s,c) => s + (parseFloat(c.liter || c.quantity) || 0), 0);
  const totAmt = allCols.reduce((s,c) => s + (parseFloat(c.amount) || 0), 0);
  const mTotLit = mornings.reduce((s,c) => s + (parseFloat(c.liter || c.quantity) || 0), 0);
  const mTotAmt = mornings.reduce((s,c) => s + (parseFloat(c.amount) || 0), 0);
  const eTotLit = evenings.reduce((s,c)  => s + (parseFloat(c.liter || c.quantity) || 0), 0);
  const eTotAmt = evenings.reduce((s,c)  => s + (parseFloat(c.amount) || 0), 0);

  const deductions = row.deductions || [];
  const fDay = d => (d || '').split('T')[0].split('-')[2] || '';

  if (is2inch) {
    return `
      <div class="bill thermal">
        <div class="th-center th-bold">${centerName||''}</div>
        <div class="th-center th-small">${centerCode||''}</div>
        <div class="th-center th-small">${dateLabel}</div>
        <div class="th-div"></div>
        <div class="th-row"><span>${row.code}</span><span>${row.name}</span></div>
        <div class="th-div"></div>
        ${allCols.length > 0 ? `
        <table class="th-table">
          <tr><th>Dt</th><th>S</th><th>Lit</th><th>F</th><th>S</th><th>Rt</th><th>Amt</th></tr>
          ${allCols.map(c => `<tr>
            <td>${fDay(c.date || c.collectionDate)}</td>
            <td>${c.shiftLabel}</td>
            <td>${parseFloat(c.liter||c.quantity||0).toFixed(3)}</td>
            <td>${parseFloat(c.fat||0).toFixed(1)}</td>
            <td>${parseFloat(c.snf||0).toFixed(1)}</td>
            <td>${fmt2(c.rate)}</td>
            <td>${fmt2(c.amount)}</td>
          </tr>`).join('')}
          <tr class="th-tot">
            <td colspan="2">Total</td>
            <td>${totLit.toFixed(3)}</td><td></td><td></td><td></td>
            <td>${fmt2(totAmt)}</td>
          </tr>
        </table>` : `
        <div class="th-row"><span>Total Ltr</span><span>${parseFloat(row.quantity||0).toFixed(3)}</span></div>
        <div class="th-row"><span>Amount</span><span>${fmt2(row.milkAmount)}</span></div>`}
        <div class="th-div"></div>
        ${deductions.map(d=>`<div class="th-row"><span>${d.type}</span><span>${fmt2(d.amount)}</span></div>`).join('')}
        <div class="th-div"></div>
        <div class="th-row th-bold"><span>Payment</span><span>Rs.${fmt2(row.payment)}</span></div>
        ${row.accountNumber?`<div class="th-small th-center">A/c: ${row.accountNumber}</div>`:''}
        <div class="th-div"></div>
      </div>`;
  }

  // ─── A4 landscape bill ─────────────────────────────────────────
  // ✅ Show each collection as its own row in the correct Morning or Evening column
  // Build date list from all collections (including duplicates on same date)
  // Each row = one collection entry, placed in Morning or Evening column by shift

  // Build rows: for dates with multiple entries of same shift, show each separately
  // Strategy: pair morning+evening by index per date, extra entries get their own row
  const byDate = {};
  allCols.forEach(c => {
    const d = c.date || c.collectionDate?.split('T')[0] || '';
    if (!byDate[d]) byDate[d] = { mornings: [], evenings: [] };
    if (c.shiftLabel === 'M') byDate[d].mornings.push(c);
    else byDate[d].evenings.push(c);
  });

  const tableRows = [];
  Object.keys(byDate).sort().forEach(date => {
    const ms = byDate[date].mornings;
    const es = byDate[date].evenings;
    const maxLen = Math.max(ms.length, es.length);
    for (let i = 0; i < maxLen; i++) {
      tableRows.push({ date, m: ms[i] || null, e: es[i] || null });
    }
  });

  return `
    <div class="bill a4-bill">
      <table class="header-table">
        <tr>
          <td style="width:60%"><div class="center-name">${centerName||''}</div><div class="center-sub">${centerCode||''}</div></td>
          <td style="text-align:right"><div>Bill No. ${row.billNo||''}</div><div>${row.guruInfo||''}</div></td>
        </tr>
      </table>
      <table class="header-table" style="margin-top:6px">
        <tr>
          <td><b>${row.code} - ${row.name}</b></td>
          <td style="text-align:right"><b>Date: ${dateLabel}</b></td>
        </tr>
      </table>

      ${tableRows.length > 0 ? `
      <table class="data-table" style="margin-top:6px">
        <thead>
          <tr>
            <th>Date</th><th>M</th>
            <th colspan="5" style="text-align:center;border-left:1px solid #aaa">Morning</th>
            <th colspan="5" style="text-align:center;border-left:1px solid #aaa">Evening</th>
          </tr>
          <tr>
            <th></th><th></th>
            <th>Liter</th><th>FAT</th><th>SNF</th><th>Rate</th><th>Amount</th>
            <th style="border-left:1px solid #aaa">Liter</th><th>FAT</th><th>SNF</th><th>Rate</th><th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows.map(dr => `<tr>
            <td>${fDay(dr.date)}</td>
            <td>${dr.m ? 'C' : ''}</td>
            <td class="r">${dr.m ? parseFloat(dr.m.liter||dr.m.quantity||0).toFixed(3) : ''}</td>
            <td class="r">${dr.m ? parseFloat(dr.m.fat||0).toFixed(1) : ''}</td>
            <td class="r">${dr.m ? parseFloat(dr.m.snf||0).toFixed(1) : ''}</td>
            <td class="r">${dr.m ? fmt2(dr.m.rate) : ''}</td>
            <td class="r">${dr.m ? fmt2(dr.m.amount) : ''}</td>
            <td class="r bl">${dr.e ? parseFloat(dr.e.liter||dr.e.quantity||0).toFixed(3) : ''}</td>
            <td class="r">${dr.e ? parseFloat(dr.e.fat||0).toFixed(1) : ''}</td>
            <td class="r">${dr.e ? parseFloat(dr.e.snf||0).toFixed(1) : ''}</td>
            <td class="r">${dr.e ? fmt2(dr.e.rate) : ''}</td>
            <td class="r">${dr.e ? fmt2(dr.e.amount) : ''}</td>
          </tr>`).join('')}
          <tr class="tot-row">
            <td>Total</td><td></td>
            <td class="r">${mTotLit.toFixed(3)}</td>
            <td class="r"></td><td class="r"></td><td class="r"></td>
            <td class="r">${fmt2(mTotAmt)}</td>
            <td class="r bl">${eTotLit.toFixed(3)}</td>
            <td class="r"></td><td class="r"></td><td class="r"></td>
            <td class="r">${fmt2(eTotAmt)}</td>
          </tr>
          <tr class="grand-tot">
            <td colspan="6" style="text-align:right"><b>Grand Total</b></td>
            <td class="r">${fmt2(mTotAmt)}</td>
            <td class="bl"></td><td></td><td></td><td></td>
            <td class="r">${fmt2(eTotAmt)}</td>
          </tr>
        </tbody>
      </table>` : `
      <table class="data-table" style="margin-top:6px">
        <thead><tr><th>Description</th><th class="r">Liter</th><th class="r">Amount</th></tr></thead>
        <tbody><tr>
          <td>Milk Collection</td>
          <td class="r">${parseFloat(row.quantity||0).toFixed(3)}</td>
          <td class="r">${fmt2(row.milkAmount)}</td>
        </tr></tbody>
      </table>`}

      <table class="data-table" style="margin-top:4px">
        <thead>
          <tr>
            <th>Deduction Type</th><th class="r">Total Balance</th>
            <th class="r">Deduction</th><th class="r">Balance</th><th class="r">Deposit</th>
          </tr>
        </thead>
        <tbody>
          ${deductions.length > 0 ? deductions.map(d => `<tr>
            <td>${d.type}</td>
            <td class="r">${fmt2(d.balance||0)}</td>
            <td class="r">${fmt2(d.amount||0)}</td>
            <td class="r">${fmt2((d.balance||0)-(d.amount||0))}</td>
            <td class="r">-</td>
          </tr>`).join('') : `<tr>
            <td>Advance</td>
            <td class="r">${fmt2(row.balance||0)}</td>
            <td class="r">0.00</td>
            <td class="r">${fmt2(row.balance||0)}</td>
            <td class="r">-</td>
          </tr>`}
          <tr class="tot-row">
            <td><b>Total</b></td><td class="r">-</td>
            <td class="r">${fmt2(row.totalDeduction||0)}</td>
            <td class="r">${fmt2(row.balance||0)}</td>
            <td class="r">0.00</td>
          </tr>
        </tbody>
      </table>

      <div class="summary-row">
        <span>Previous Balance: <b>${fmt2(row.balance||0)}</b></span>
        <span>Amount: <b>${fmt2(row.milkAmount||0)}</b></span>
        <span>Deduction: <b>${fmt2(row.totalDeduction||0)}</b></span>
        <span>Payment: <b>${fmt2(row.payment||0)}</b></span>
        <span>Current Balance: <b>- ${fmt2(Math.abs(row.balance||0))}</b></span>
      </div>
      ${row.accountNumber ? `<div class="bank-info">Bank Details: ${row.accountNumber||''}/${row.ifscCode||''}</div>` : ''}
    </div>`;
}

function printFarmers(rows, fromDate, toDate, centerName, centerCode, printerType) {
  const is2inch = printerType === '2inch';
  const css = is2inch ? `
    @page { size: 58mm auto; margin: 0; }
    * { box-sizing:border-box; margin:0; padding:0; font-family:'Courier New',monospace; }
    body { width:56mm; font-size:8px; padding:1mm; }
    .bill { width:56mm; }
    .th-center { text-align:center; }
    .th-bold { font-weight:bold; }
    .th-small { font-size:7px; }
    .th-div { border-top:1px dashed #000; margin:1.5mm 0; }
    .th-row { display:flex; justify-content:space-between; margin:0.5mm 0; }
    .th-table { width:100%; border-collapse:collapse; font-size:7px; }
    .th-table th { border-bottom:1px solid #000; padding:0.5mm; }
    .th-table td { padding:0.3mm; }
    .th-tot td { font-weight:bold; border-top:1px solid #000; }
    @media print { body{width:56mm;} }
  ` : `
    @page { size: A4 landscape; margin: 8mm; }
    * { box-sizing:border-box; margin:0; padding:0; font-family:Arial,sans-serif; }
    body { font-size:9px; }
    .bill { page-break-after:always; padding:4px; }
    .bill:last-child { page-break-after:auto; }
    .center-name { font-size:13px; font-weight:bold; }
    .center-sub { font-size:9px; color:#555; }
    .header-table { width:100%; border-collapse:collapse; }
    .data-table { width:100%; border-collapse:collapse; font-size:8px; }
    .data-table th { background:#e8f0fe; padding:2px 4px; border:1px solid #bbb; font-size:8px; white-space:nowrap; }
    .data-table td { padding:1.5px 4px; border:1px solid #ddd; white-space:nowrap; }
    .r { text-align:right; }
    .bl { border-left:1px solid #aaa!important; }
    .tot-row td { font-weight:bold; background:#f0f0f0; }
    .grand-tot td { font-weight:bold; background:#e8f0fe; }
    .summary-row { display:flex; justify-content:space-between; margin-top:4px; font-size:8px; border:1px solid #bbb; padding:3px 6px; background:#f9f9f9; }
    .bank-info { font-size:8px; margin-top:3px; color:#444; }
    @media print { body{font-size:9px;} }
  `;
  const billsHtml = rows.map(row => buildFarmerBill(row, fromDate, toDate, centerName, centerCode, printerType)).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>${css}</style></head><body>${billsHtml}</body></html>`;
  const w = window.open('', '_blank', 'width=1000,height=700');
  w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => w.print(), 500);
}

// ✅ FIX: Fetch detail — tries multiple endpoints, handles flat collection arrays
async function fetchFarmerDetail(farmerId, fromDate, toDate) {
  // ✅ CONFIRMED via API inspection:
  // - /collection ignores farmerId AND fromDate/toDate params — returns ALL collections for ALL farmers
  // - Must filter client-side by both farmerId AND date range
  // - Field names: quantity (not liter), collectionDate (not date), shift=1/2 (not M/E)
  try {
    const res = await api.get(`/collection?farmerId=${farmerId}&fromDate=${fromDate}&toDate=${toDate}&pageSize=500`);
    if (res.data?.success && res.data?.data) {
      const allRows = res.data.data?.data || res.data.data || [];

      // ✅ Filter by farmerId (API ignores this param)
      const byFarmer = allRows.filter(c => Number(c.farmerId) === Number(farmerId));

      // ✅ Filter by date range (API ignores fromDate/toDate params)
      const from = new Date(fromDate);
      const to   = new Date(toDate);
      to.setHours(23, 59, 59); // include full last day

      const inRange = byFarmer.filter(c => {
        const d = new Date((c.collectionDate || c.date || '').split('T')[0]);
        return d >= from && d <= to;
      });

      // ✅ Normalize field names for buildFarmerBill
      const normalize = c => ({
        ...c,
        liter: c.quantity  || c.liter  || 0,
        date:  (c.collectionDate || c.date || '').split('T')[0],
        fat:   c.fat   || 0,
        snf:   c.snf   || 0,
        rate:  c.rate  || 0,
        amount: c.amount || 0,
      });

      const normalized = inRange.map(normalize);

      return {
        collections:        normalized,
        morningCollections: normalized.filter(c => c.shift === 1 || c.shift === 'M' || c.shiftName === 'Morning'),
        eveningCollections: normalized.filter(c => c.shift === 2 || c.shift === 'E' || c.shiftName === 'Evening'),
      };
    }
  } catch(e) {
    console.error('fetchFarmerDetail error:', e);
  }
  return null;
}

// ═════════════════════════════════════════════════════
//  MAIN INVOICE COMPONENT
// ═════════════════════════════════════════════════════
export default function Invoice() {
  const { theme } = useApp();
  const { user }  = useAuth();

  const [periods,     setPeriods]     = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [fromDate,    setFromDate]    = useState('');
  const [toDate,      setToDate]      = useState('');
  const [rows,        setRows]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(100);
  const [selected,    setSelected]    = useState(new Set());
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [view,        setView]        = useState('main');
  const [loaded,      setLoaded]      = useState(false);
  const [printerType, setPrinterType] = useState('A4');

  const [previewOpen,   setPreviewOpen]   = useState(false);
  const [previewRow,    setPreviewRow]    = useState(null);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  useEffect(() => {
    api.get('/center-settings').then(res => {
      const list = res.data?.data || [];
      const find = k => (list.find(s=>s.settingKey===k||s.SettingKey===k)?.settingValue||'');
      const period = find('paymentPeriod')||find('billPeriod')||'01-15, 16-ME';
      const gen = generatePeriods(period, 24);
      setPeriods(gen);
      if (gen.length>0) { setSelectedKey(gen[0].fromDate+'|'+gen[0].toDate); setFromDate(gen[0].fromDate); setToDate(gen[0].toDate); }
    }).catch(() => {
      const gen = generatePeriods('01-15, 16-ME', 24);
      setPeriods(gen);
      if (gen.length>0) { setSelectedKey(gen[0].fromDate+'|'+gen[0].toDate); setFromDate(gen[0].fromDate); setToDate(gen[0].toDate); }
    });
  }, []);

  const handlePeriodChange = (e) => {
    const key = e.target.value;
    setSelectedKey(key);
    const [fd,td] = key.split('|');
    setFromDate(fd); setToDate(td);
    setRows([]); setLoaded(false); setSelected(new Set());
  };

  const fetchData = useCallback(async (fd, td, pg, ps, srch) => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/invoice/projected', { params:{ fromDate:fd, toDate:td, page:pg, pageSize:ps, search:srch||'' } });
      if (res.data?.success) {
        setRows(res.data.data?.data||[]);
        setTotal(res.data.data?.totalCount??0);
        setSelected(new Set()); setLoaded(true);
      } else setError(res.data?.message||'Load failed.');
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const handleLoad = () => fetchData(fromDate, toDate, page, pageSize, search);

  const handleRefreshRow = async (farmerId) => {
    try {
      const res = await api.get('/invoice/projected', { params:{ fromDate, toDate, page:1, pageSize:1, search:farmerId.toString() } });
      if (res.data?.success) {
        const upd = res.data.data?.data?.[0];
        if (upd) setRows(prev=>prev.map(r=>r.farmerId===farmerId?upd:r));
      }
    } catch {}
  };

  // ✅ FIX: Simple toggle — no double-trigger issue
  const toggleSelect = useCallback((id) => {
    setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  }, []);

  const toggleAll = () => {
    setSelected(selected.size===rows.length ? new Set() : new Set(rows.map(r=>r.farmerId)));
  };

  const handleGenerate = async () => {
    const toGen = rows.filter(r=>selected.has(r.farmerId));
    if (!toGen.length) { setError('Select at least one farmer.'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/invoice/generate', {
        fromDate, toDate,
        rows: toGen.map(r=>({ farmerId:r.farmerId, quantity:r.quantity, milkAmount:r.milkAmount,
          totalDeduction:r.totalDeduction, totalAmount:r.totalAmount, balance:r.balance, payment:r.payment }))
      });
      if (res.data?.success) { setSuccess(res.data.message||'Invoices generated!'); setTimeout(()=>setSuccess(''),4000); }
      else setError(res.data?.message||'Generate failed.');
    } catch(e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  const handlePreview = async (row) => {
    setPreviewRow(row); setPreviewDetail(null); setPreviewOpen(true); setLoadingDetail(true);
    const detail = await fetchFarmerDetail(row.farmerId, fromDate, toDate);
    setPreviewDetail(detail ? {
      ...row,
      morningCollections: detail.morningCollections || [],
      eveningCollections: detail.eveningCollections || [],
      collections: detail.collections || [],
      deductions: detail.deductions || row.deductions || [],
      avgFat:  detail.avgFat  || row.avgFat,
      avgSnf:  detail.avgSnf  || row.avgSnf,
      avgRate: detail.avgRate || row.avgRate,
      billNo:  detail.billNo  || row.billNo,
    } : row);
    setLoadingDetail(false);
  };

  // ✅ FIX: Fetch collections before printing
  const handlePrintSelected = async () => {
    const toPrint = rows.filter(r=>selected.has(r.farmerId));
    if (!toPrint.length) { setError('Select farmers to print.'); return; }
    setGenerating(true);
    try {
      const detailed = await Promise.all(
        toPrint.map(async row => {
          const detail = await fetchFarmerDetail(row.farmerId, fromDate, toDate);
          // ✅ Preserve original row fields (farmerId, name, code, payment etc)
          // Only take collection arrays from detail, not summary numbers which may be wrong
          if (detail) {
            return {
              ...row,
              morningCollections: detail.morningCollections || [],
              eveningCollections: detail.eveningCollections || [],
              collections: detail.collections || [],
              deductions: detail.deductions || row.deductions || [],
              avgFat:  detail.avgFat  || row.avgFat,
              avgSnf:  detail.avgSnf  || row.avgSnf,
              avgRate: detail.avgRate || row.avgRate,
              billNo:  detail.billNo  || row.billNo,
              guruInfo: detail.guruInfo || row.guruInfo,
            };
          }
          return row;
        })
      );
      printFarmers(detailed, fromDate, toDate, user?.centerName, user?.centerCode, printerType);
    } finally { setGenerating(false); }
  };

  // ✅ FIX: Always fetch detail before single print
  const handlePrintSingle = async (row, existingDetail) => {
    const hasCollections = existingDetail && (
      existingDetail.morningCollections?.length > 0 ||
      existingDetail.eveningCollections?.length > 0 ||
      existingDetail.collections?.length > 0
    );
    if (hasCollections) {
      printFarmers([{ ...row, ...existingDetail }], fromDate, toDate, user?.centerName, user?.centerCode, printerType);
      return;
    }
    const detail = await fetchFarmerDetail(row.farmerId, fromDate, toDate);
    const mergedRow = detail ? {
      ...row,
      morningCollections: detail.morningCollections || [],
      eveningCollections: detail.eveningCollections || [],
      collections: detail.collections || [],
      deductions: detail.deductions || row.deductions || [],
      avgFat:  detail.avgFat  || row.avgFat,
      avgSnf:  detail.avgSnf  || row.avgSnf,
      avgRate: detail.avgRate || row.avgRate,
      billNo:  detail.billNo  || row.billNo,
    } : row;
    printFarmers([mergedRow], fromDate, toDate, user?.centerName, user?.centerCode, printerType);
  };

  const handleDelete = (row) => setDeleteTarget(row);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/invoice/${deleteTarget.farmerId}`, { params:{ fromDate, toDate } });
      if (res.data?.success) {
        setSuccess('Invoice deleted.');
        setRows(prev=>prev.filter(r=>r.farmerId!==deleteTarget.farmerId));
        setDeleteTarget(null);
      } else setError(res.data?.message||'Delete failed.');
    } catch(e) { setError(e.message||'Delete failed.'); }
    finally { setDeleting(false); }
  };

  const totQty  = rows.reduce((s,r)=>s+(parseFloat(r.quantity)||0),0);
  const totDed  = rows.reduce((s,r)=>s+(parseFloat(r.totalDeduction)||0),0);
  const totCred = rows.reduce((s,r)=>s+(parseFloat(r.totalCredit)||0),0);
  const totMilk = rows.reduce((s,r)=>s+(parseFloat(r.milkAmount)||0),0);
  const totAmt  = rows.reduce((s,r)=>s+(parseFloat(r.totalAmount)||0),0);
  const totBal  = rows.reduce((s,r)=>s+(parseFloat(r.balance)||0),0);
  const totPay  = rows.reduce((s,r)=>s+(parseFloat(r.payment)||0),0);

  const handlePaymentRegisterPrint = () => {
    const html = `<html><head><style>
      body{font-family:Arial,sans-serif;font-size:9px;margin:10px}
      h3{margin:0;font-size:11px;text-align:center}.sub{text-align:center;font-size:9px;color:#555;margin-bottom:2px}
      table{width:100%;border-collapse:collapse}
      th{background:#e8f0fe;padding:3px 4px;border:1px solid #bbb;font-size:8px;white-space:nowrap}
      td{padding:2px 4px;border:1px solid #ddd;font-size:8px;white-space:nowrap}
      .r{text-align:right}.tot td{font-weight:bold;background:#f5f5f5}
    </style></head><body>
      <h3>${user?.centerName||''}</h3>
      <div class="sub">Period: ${fromDate} To ${toDate}</div>
      <table><thead><tr>
        <th>Code</th><th>Name</th><th class="r">Liter</th>
        <th class="r">Milk Amount</th><th class="r">Total Amount</th>
        <th class="r">Deduction</th><th class="r">Payment</th><th class="r">Balance</th><th>Signature</th>
      </tr></thead><tbody>
        ${rows.map(r=>`<tr>
          <td>${r.code}</td><td>${r.name}</td>
          <td class="r">${fmt3(r.quantity)}</td>
          <td class="r">${fmt2(r.milkAmount)}</td><td class="r">${fmt2(r.totalAmount)}</td>
          <td class="r">${fmt2(r.totalDeduction)}</td>
          <td class="r">${fmt2(r.payment)}</td>
          <td class="r">${fmt2(r.balance)}</td><td></td>
        </tr>`).join('')}
        <tr class="tot">
          <td>Total</td><td>${rows.length}</td>
          <td class="r">${fmt3(totQty)}</td>
          <td class="r">${fmt2(totMilk)}</td><td class="r">${fmt2(totAmt)}</td>
          <td class="r">${fmt2(totDed)}</td>
          <td class="r">${fmt2(totPay)}</td>
          <td class="r">${fmt2(totBal)}</td><td></td>
        </tr>
      </tbody></table>
    </body></html>`;
    const w=window.open('','_blank','width=1100,height=700');
    w.document.write(html); w.document.close(); w.focus(); setTimeout(()=>w.print(),400);
  };

  const handleBankPrint = () => {
    const bankRows = rows.filter(r=>r.accountNumber);
    const html = `<html><head><style>
      body{font-family:Arial,sans-serif;font-size:10px;margin:15px}
      h3{margin:0;font-size:12px}.sub{font-size:9px;color:#555}
      .title{color:#1a4fa0;font-weight:bold;font-size:11px}
      .stats{display:grid;grid-template-columns:1fr 1fr;gap:4px;margin:8px 0;font-size:9px}
      table{width:100%;border-collapse:collapse;margin-top:8px}
      th{background:#e8f0fe;padding:4px 6px;border:1px solid #bbb;font-size:9px}
      td{padding:3px 6px;border:1px solid #ddd;font-size:9px}.r{text-align:right}
    </style></head><body>
      <div style="display:flex;justify-content:space-between">
        <div><h3>${user?.centerName||''}</h3><div class="sub">${user?.centerCode||''}</div></div>
        <div class="title">NEFT/RTGS Transfer File<br/><span style="font-size:9px;font-weight:normal">${fromDate} To ${toDate}</span></div>
      </div>
      <div class="stats">
        <div>Lowest: ${bankRows.length?Math.min(...bankRows.map(r=>parseFloat(r.payment)||0)):0}</div>
        <div>Count: ${bankRows.length}</div>
        <div>Highest: ${bankRows.length?Math.max(...bankRows.map(r=>parseFloat(r.payment)||0)):0}</div>
        <div>Total: ${fmt2(bankRows.reduce((s,r)=>s+(parseFloat(r.payment)||0),0))}</div>
      </div>
      <table><thead><tr>
        <th>Sr</th><th>Name</th><th>Account Number</th>
        <th>Bank</th><th>Branch</th><th>IFSC</th><th class="r">Payment</th>
      </tr></thead><tbody>
        ${bankRows.map((r,i)=>`<tr>
          <td>${i+1}</td><td>${r.name}</td><td>${r.accountNumber||''}</td>
          <td>${r.bankName||''}</td><td>${r.branch||''}</td><td>${r.ifscCode||''}</td>
          <td class="r">${fmt2(r.payment)}</td>
        </tr>`).join('')}
      </tbody></table>
    </body></html>`;
    const w=window.open('','_blank','width=900,height=600');
    w.document.write(html); w.document.close(); w.focus(); setTimeout(()=>w.print(),400);
  };

  const thS = (align,bg) => ({ fontSize:'0.75rem', fontWeight:700, color:'#374151', padding:'8px 10px', borderBottom:'1px solid #e5e7eb', background:bg||'#f3f4f6', whiteSpace:'nowrap', textAlign:align||'left' });
  const tdS = (bold,align,color) => ({ fontSize:'0.78rem', padding:'7px 10px', borderBottom:'1px solid #f3f4f6', whiteSpace:'nowrap', fontWeight:bold?700:400, textAlign:align||'left', color:color||'#111827' });
  const totalPages = Math.max(1,Math.ceil(total/pageSize));
  const from = total===0?0:(page-1)*pageSize+1;
  const to   = Math.min(page*pageSize,total);

  if (view==='bankfile') {
    return <BankPaymentFile rows={rows} fromDate={fromDate} toDate={toDate} user={user} theme={theme} onBack={()=>setView('main')}/>;
  }

  return (
    <Layout title="Invoice">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={()=>setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={()=>setSuccess('')}>{success}</Alert>}

      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Print sx={{ color:theme.primary }}/>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Invoice</Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center' }}>
          <ToggleButtonGroup value={printerType} exclusive size="small"
            onChange={(_,v)=>{ if(v) setPrinterType(v); }}
            sx={{ '& .MuiToggleButton-root':{ fontSize:'0.72rem', py:0.4, px:1.2, fontWeight:700, '&.Mui-selected':{ background:theme.primary, color:'#fff' } } }}>
            <ToggleButton value="A4"><PrintOutlined sx={{ fontSize:14, mr:0.5 }}/>A4</ToggleButton>
            <ToggleButton value="2inch"><PrintOutlined sx={{ fontSize:14, mr:0.5 }}/>2 inch</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" size="small" startIcon={<Print sx={{ fontSize:15 }}/>}
            onClick={handlePaymentRegisterPrint} disabled={rows.length===0}
            sx={{ borderRadius:1.5, textTransform:'none', fontSize:'0.78rem', borderColor:theme.primary, color:theme.primary }}>
            Payment Register
          </Button>
          <Button variant="outlined" size="small" startIcon={<Print sx={{ fontSize:15 }}/>}
            onClick={handleBankPrint} disabled={rows.length===0}
            sx={{ borderRadius:1.5, textTransform:'none', fontSize:'0.78rem', borderColor:theme.primary, color:theme.primary }}>
            Bank Payment Print
          </Button>
          <Button variant="outlined" size="small"
            onClick={()=>setView('bankfile')} disabled={rows.length===0}
            sx={{ borderRadius:1.5, textTransform:'none', fontSize:'0.78rem', borderColor:theme.primary, color:theme.primary }}>
            Bank Payment File
          </Button>
        </Box>
      </Box>

      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2, flexWrap:'wrap' }}>
        <Typography sx={{ fontSize:'0.85rem', fontWeight:600, whiteSpace:'nowrap' }}>Payment Period :</Typography>
        <FormControl size="small" sx={{ minWidth:280 }}>
          <Select value={selectedKey} onChange={handlePeriodChange} displayEmpty IconComponent={ExpandMore}
            sx={{ fontSize:'0.85rem', fontWeight:600, '& .MuiSelect-select':{ py:0.9 },
              '& .MuiOutlinedInput-notchedOutline':{ borderColor:theme.primary+'66' },
              '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:theme.primary },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline':{ borderColor:theme.primary } }}
            MenuProps={{ PaperProps:{ sx:{ maxHeight:380, borderRadius:2, boxShadow:'0 4px 20px rgba(0,0,0,0.12)' } } }}>
            {periods.map((p,i)=>(
              <MenuItem key={i} value={p.fromDate+'|'+p.toDate}
                sx={{ fontSize:'0.83rem', fontWeight:i===0?700:400, color:i===0?theme.primary:'inherit',
                  background:i===0?(theme.light||'#ECFDF5'):'transparent',
                  '&.Mui-selected':{ background:theme.light||'#ECFDF5', color:theme.primary, fontWeight:700 },
                  '&:hover':{ background:theme.light||'#ECFDF5' } }}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleLoad} disabled={loading||!selectedKey}
          sx={{ borderRadius:2, fontWeight:700, textTransform:'none', background:`linear-gradient(135deg,${theme.primary},${theme.hover||theme.primary})` }}>
          {loading?<CircularProgress size={16} sx={{ color:'#fff' }}/>:'Generate Invoice'}
        </Button>
        {loaded && rows.length>0 && (
          <>
            <Button variant="contained" onClick={handleGenerate} disabled={generating||selected.size===0}
              sx={{ borderRadius:2, fontWeight:700, textTransform:'none', background:'#16a34a', '&:hover':{ background:'#15803d' } }}>
              {generating?<CircularProgress size={16} sx={{ color:'#fff' }}/>:`Save Selected (${selected.size})`}
            </Button>
            {selected.size>0 && (
              <Button variant="outlined" onClick={handlePrintSelected} disabled={generating}
                startIcon={generating?<CircularProgress size={14}/>:<Print sx={{ fontSize:15 }}/>}
                sx={{ borderRadius:2, fontWeight:700, textTransform:'none', borderColor:theme.primary, color:theme.primary }}>
                {generating?'Loading...': `Print Selected (${selected.size})`}
              </Button>
            )}
          </>
        )}
      </Box>

      {loaded && (
        <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border||'#e5e7eb'}` }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
              <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>Invoices</Typography>
              <Chip label={`Selected : ${selected.size}`} size="small"
                sx={{ fontSize:'0.75rem', background:selected.size>0?theme.light:'#f3f4f6', color:theme.primary, fontWeight:600 }}/>
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              {selected.size>0 && (
                <Chip label={`Print ${selected.size} via ${printerType}`} size="small" onClick={handlePrintSelected}
                  sx={{ background:theme.primary, color:'#fff', fontWeight:700, fontSize:'0.72rem', cursor:'pointer' }}/>
              )}
              <TextField size="small" placeholder="Search Invoice" value={search}
                onChange={e=>{ setSearch(e.target.value); fetchData(fromDate,toDate,1,pageSize,e.target.value); }}
                sx={{ width:200, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
            </Box>
          </Box>

          <Box sx={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th colSpan={3} style={{ ...thS('center','#f9fafb'), borderRight:'1px solid #e5e7eb' }}></th>
                  <th colSpan={2} style={{ ...thS('center','#eff6ff'), borderRight:'1px solid #e5e7eb' }}>Farmer Info</th>
                  <th style={{ ...thS('center','#f0fdf4'), borderRight:'1px solid #e5e7eb' }}>Milks</th>
                  <th style={{ ...thS('center','#fef9c3'), borderRight:'1px solid #e5e7eb' }}>Deduction</th>
                  <th style={{ ...thS('center','#f0fdf4'), borderRight:'1px solid #e5e7eb' }}>Credit</th>
                  <th colSpan={3} style={thS('center','#eff6ff')}>Payment</th>
                  <th style={thS('center','#f9fafb')}>Actions</th>
                </tr>
                <tr>
                  <th style={{ ...thS(), width:36 }}>
                    <Checkbox size="small" checked={selected.size===rows.length&&rows.length>0}
                      indeterminate={selected.size>0&&selected.size<rows.length} onChange={toggleAll}
                      sx={{ p:0, color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>
                  </th>
                  <th style={{ ...thS(), width:60 }}>Code</th>
                  <th style={thS()}>Name</th>
                  <th style={thS('right')}>Quantity</th>
                  <th style={{ ...thS('right'), borderRight:'1px solid #e5e7eb' }}>Total Deduction (A)</th>
                  <th style={{ ...thS('right'), borderRight:'1px solid #e5e7eb' }}>Total Credit (B)</th>
                  <th style={thS('right')}>Milk Amount (C)</th>
                  <th style={thS('right')}>Total Amount (B+C)</th>
                  <th style={thS('right')}>Balance</th>
                  <th style={thS('right')}>Payment (B+C-A)</th>
                  <th style={{ ...thS(), width:36 }}></th>
                  <th style={{ ...thS('center'), width:110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({length:8}).map((_,i)=>(
                      <tr key={i}>{Array.from({length:12}).map((__,j)=>(<td key={j} style={{ padding:'8px 10px' }}><Skeleton height={14}/></td>))}</tr>
                    ))
                  : rows.length===0
                    ? <tr><td colSpan={12} style={{ textAlign:'center', padding:'48px', color:'#9ca3af', fontSize:'0.85rem' }}>No Records Found.</td></tr>
                    : <>
                      {rows.map((row,idx)=>(
                        <tr key={row.farmerId}
                          style={{ background:selected.has(row.farmerId)?'#eff6ff':idx%2===0?'#fff':'#fafafa', cursor:'pointer' }}
                          onClick={()=>toggleSelect(row.farmerId)}>
                          {/* ✅ FIX: stopPropagation on checkbox cell only — prevents double-toggle */}
                          <td style={{ ...tdS(), textAlign:'center' }} onClick={e=>e.stopPropagation()}>
                            <Checkbox size="small" checked={selected.has(row.farmerId)}
                              onChange={()=>toggleSelect(row.farmerId)}
                              sx={{ p:0, color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>
                          </td>
                          <td style={tdS(true)}>{row.code}</td>
                          <td style={tdS(true)}>{row.name}</td>
                          <td style={tdS(false,'right')}>{fmt3(row.quantity)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.totalDeduction)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.totalCredit)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.milkAmount)}</td>
                          <td style={tdS(false,'right')}>{fmt2(row.totalAmount)}</td>
                          <td style={tdS(false,'right',parseFloat(row.balance)>0?'#dc2626':'#111')}>{fmt2(row.balance)}</td>
                          <td style={tdS(true,'right')}>{fmt2(row.payment)}</td>
                          <td style={{ ...tdS(), textAlign:'center' }} onClick={e=>e.stopPropagation()}>
                            <Tooltip title="Refresh">
                              <IconButton size="small" onClick={()=>handleRefreshRow(row.farmerId)} sx={{ p:0.3, color:theme.primary }}>
                                <Refresh sx={{ fontSize:15 }}/>
                              </IconButton>
                            </Tooltip>
                          </td>
                          <td style={{ ...tdS(), textAlign:'center' }} onClick={e=>e.stopPropagation()}>
                            <Box sx={{ display:'flex', gap:0.3, justifyContent:'center' }}>
                              <Tooltip title="Preview">
                                <IconButton size="small" onClick={()=>handlePreview(row)}
                                  sx={{ p:0.4, color:'#2563EB', border:'1px solid #bfdbfe', borderRadius:1.5 }}>
                                  <Visibility sx={{ fontSize:14 }}/>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Print (${printerType})`}>
                                <IconButton size="small" onClick={()=>handlePrintSingle(row,null)}
                                  sx={{ p:0.4, color:theme.primary, border:`1px solid ${theme.primary}44`, borderRadius:1.5 }}>
                                  <Print sx={{ fontSize:14 }}/>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Invoice">
                                <IconButton size="small" onClick={()=>handleDelete(row)}
                                  sx={{ p:0.4, color:'#DC2626', border:'1px solid #fca5a5', borderRadius:1.5 }}>
                                  <Delete sx={{ fontSize:14 }}/>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background:'#f9fafb', borderTop:'2px solid #e5e7eb' }}>
                        <td style={tdS()} colSpan={2}></td>
                        <td style={tdS(true)}>Total</td>
                        <td style={tdS(true,'right')}>{fmt3(totQty)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totDed)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totCred)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totMilk)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totAmt)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totBal)}</td>
                        <td style={tdS(true,'right')}>{fmt2(totPay)}</td>
                        <td style={tdS()} colSpan={2}></td>
                      </tr>
                    </>
                }
              </tbody>
            </table>
          </Box>

          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0.5, px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
            <Typography sx={{ fontSize:'0.78rem', color:'#6b7280', mr:2 }}>Showing {from} to {to} of {total} entries</Typography>
            {[{lbl:'«',t:1,d:page<=1},{lbl:'‹',t:page-1,d:page<=1},{lbl:'›',t:page+1,d:page>=totalPages},{lbl:'»',t:totalPages,d:page>=totalPages}].map(({lbl,t,d})=>(
              <IconButton key={lbl} size="small" disabled={d}
                onClick={()=>{ setPage(t); fetchData(fromDate,toDate,t,pageSize,search); }}
                sx={{ width:28, height:28, border:'1px solid #e5e7eb', borderRadius:1 }}>
                <Typography sx={{ fontSize:12, fontWeight:700 }}>{lbl}</Typography>
              </IconButton>
            ))}
            <Box sx={{ mx:1, px:2, py:0.4, background:theme.primary, color:'#fff', borderRadius:1, fontSize:'0.78rem', fontWeight:700, textAlign:'center', minWidth:28 }}>{page}</Box>
            <select value={pageSize}
              onChange={e=>{ setPageSize(Number(e.target.value)); setPage(1); fetchData(fromDate,toDate,1,Number(e.target.value),search); }}
              style={{ marginLeft:4, fontSize:'0.78rem', padding:'3px 6px', borderRadius:4, border:'1px solid #d1d5db', background:'#fff' }}>
              {[20,50,100,200].map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </Box>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={()=>setPreviewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', py:1.5, background:theme.light, borderBottom:`1px solid ${theme.primary}33`, fontWeight:700, color:theme.primary, fontSize:'0.95rem' }}>
          Preview — {previewRow?.name}
          <Box sx={{ display:'flex', gap:1 }}>
            <Button size="small" variant="contained" startIcon={<Print sx={{ fontSize:14 }}/>}
              onClick={()=>handlePrintSingle(previewRow, previewDetail)}
              sx={{ borderRadius:1.5, fontWeight:700, fontSize:'0.75rem', background:`linear-gradient(135deg,${theme.primary},${theme.hover||theme.primary})` }}>
              Print ({printerType})
            </Button>
            <IconButton size="small" onClick={()=>setPreviewOpen(false)}><Close fontSize="small"/></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p:2, background:'#fafafa' }}>
          {loadingDetail ? (
            <Box sx={{ textAlign:'center', py:4 }}>
              <CircularProgress sx={{ color:theme.primary }}/>
              <Typography sx={{ mt:2, color:'#888' }}>Loading details...</Typography>
            </Box>
          ) : previewDetail ? (
            <Box sx={{ background:'#fff', border:'1px solid #e0e0e0', borderRadius:1, p:2 }}>
              <Box sx={{ display:'flex', justifyContent:'space-between', mb:1 }}>
                <Box>
                  <Typography sx={{ fontWeight:800, fontSize:'1rem' }}>{user?.centerName||''}</Typography>
                  <Typography variant="caption" sx={{ color:'#666' }}>{user?.centerCode||''}</Typography>
                </Box>
                <Box sx={{ textAlign:'right' }}>
                  <Typography variant="caption" sx={{ display:'block' }}>Bill No. {previewDetail.billNo||''}</Typography>
                </Box>
              </Box>
              <Box sx={{ display:'flex', justifyContent:'space-between', mb:1 }}>
                <Typography variant="body2" sx={{ fontWeight:700 }}>{previewDetail.code} - {previewDetail.name}</Typography>
                <Typography variant="body2" sx={{ fontWeight:700 }}>
                  Date: {fromDate.split('-').reverse().join('/')} To {toDate.split('-').reverse().join('/')}
                </Typography>
              </Box>

              {/* ✅ Show daily collections in preview */}
              {([...(previewDetail.morningCollections||[]).map(c=>({...c,sl:'M'})),
                 ...(previewDetail.eveningCollections||[]).map(c=>({...c,sl:'E'})),
                 ...(previewDetail.collections||[]).map(c=>({...c,sl:c.shift===1||c.shift==='M'?'M':'E'}))
              ].filter((c,i,a)=>a.findIndex(x=>x.sl===c.sl&&(x.date||x.collectionDate)===(c.date||c.collectionDate))===i).length > 0) && (
                <Box sx={{ overflowX:'auto', mb:1 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.72rem' }}>
                    <thead>
                      <tr style={{ background:'#e8f0fe' }}>
                        {['Date','S','Liter','FAT','SNF','Rate','Amount'].map(h=>(
                          <th key={h} style={{ padding:'3px 6px', border:'1px solid #bbb', textAlign:['Liter','FAT','SNF','Rate','Amount'].includes(h)?'right':'center' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...(previewDetail.morningCollections||[]).map(c=>({...c,sl:'M'})),
                        ...(previewDetail.eveningCollections||[]).map(c=>({...c,sl:'E'})),
                        ...(previewDetail.collections||[]).map(c=>({...c,sl:c.shift===1||c.shift==='M'?'M':'E'}))
                      ].filter((c,i,a)=>a.findIndex(x=>x.sl===c.sl&&(x.date||x.collectionDate)===(c.date||c.collectionDate))===i)
                       .sort((a,b)=>(a.date||a.collectionDate||'').localeCompare(b.date||b.collectionDate||''))
                       .map((c,i)=>(
                        <tr key={i} style={{ background:i%2===0?'#fff':'#f9f9f9' }}>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'center' }}>{(c.date||c.collectionDate||'').split('T')[0]}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'center' }}>{c.sl}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'right' }}>{parseFloat(c.liter||c.quantity||0).toFixed(3)}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'right' }}>{parseFloat(c.fat||0).toFixed(1)}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'right' }}>{parseFloat(c.snf||0).toFixed(1)}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'right' }}>{fmt2(c.rate)}</td>
                          <td style={{ padding:'2px 6px', border:'1px solid #eee', textAlign:'right', fontWeight:600 }}>{fmt2(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}

              <Box sx={{ display:'flex', justifyContent:'space-between', p:1, background:'#f0fdf4', border:'1px solid #a7f3d0', borderRadius:1, fontSize:'0.8rem', flexWrap:'wrap', gap:1 }}>
                <span>Balance: <b>{fmt2(previewDetail.balance||0)}</b></span>
                <span>Amount: <b>{fmt2(previewDetail.milkAmount||0)}</b></span>
                <span>Deduction: <b>{fmt2(previewDetail.totalDeduction||0)}</b></span>
                <span style={{ color:'#059669', fontWeight:800 }}>Payment: <b>{fmt2(previewDetail.payment||0)}</b></span>
              </Box>
              {previewDetail.accountNumber && (
                <Typography variant="caption" sx={{ display:'block', mt:0.5, color:'#666' }}>
                  Bank: {previewDetail.accountNumber} / {previewDetail.ifscCode}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign:'center', py:3 }}>
              <Typography sx={{ color:'#888', mb:1 }}>Detailed collections not available from API.</Typography>
              {previewRow && (
                <Box sx={{ p:2, background:'#f9f9f9', borderRadius:1, textAlign:'left', fontSize:'0.82rem' }}>
                  <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.5 }}>
                    <span>Farmer: <b>{previewRow.code} - {previewRow.name}</b></span>
                    <span>Qty: <b>{fmt3(previewRow.quantity)}</b></span>
                  </Box>
                  <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.5 }}>
                    <span>Milk Amount: <b>{fmt2(previewRow.milkAmount)}</b></span>
                    <span>Deduction: <b>{fmt2(previewRow.totalDeduction)}</b></span>
                  </Box>
                  <Box sx={{ display:'flex', justifyContent:'space-between', p:1, background:'#ECFDF5', borderRadius:1 }}>
                    <span style={{ fontWeight:800, color:'#059669' }}>Payment: {fmt2(previewRow.payment)}</span>
                    <span>Balance: {fmt2(previewRow.balance)}</span>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={()=>setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ py:1.5, fontWeight:700, color:'#DC2626', background:'#FFF5F5', borderBottom:'1px solid #FECACA', fontSize:'0.95rem' }}>Delete Invoice</DialogTitle>
        <DialogContent sx={{ px:2.5, py:2 }}>
          <Typography variant="body2" sx={{ color:'#444', mb:1 }}>
            Delete invoice for <b>{deleteTarget?.name}</b> ({deleteTarget?.code})?
          </Typography>
          <Alert severity="warning" sx={{ py:0.3, borderRadius:1 }}>This cannot be undone.</Alert>
        </DialogContent>
        <DialogActions sx={{ px:2.5, py:2, gap:1 }}>
          <Button onClick={()=>setDeleteTarget(null)} variant="outlined" sx={{ borderRadius:2, color:'#888', borderColor:'#ddd', flex:1 }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" disabled={deleting}
            startIcon={deleting?<CircularProgress size={13} color="inherit"/>:<Delete/>}
            sx={{ background:'linear-gradient(135deg,#DC2626,#B91C1C)', borderRadius:2, fontWeight:700, flex:1 }}>
            {deleting?'Deleting...':'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

// ── Bank Payment File ──────────────────────────────────
function BankPaymentFile({ rows, fromDate, toDate, user, theme, onBack }) {
  const [search, setSearch] = useState('');
  const bankRows = rows.filter(r=>r.accountNumber&&(search===''||r.name.toLowerCase().includes(search.toLowerCase())||(r.accountNumber||'').includes(search)));
  const totalPay=bankRows.reduce((s,r)=>s+(parseFloat(r.payment)||0),0);
  const lowestAmt=bankRows.length?Math.min(...bankRows.map(r=>parseFloat(r.payment)||0)):0;
  const highestAmt=bankRows.length?Math.max(...bankRows.map(r=>parseFloat(r.payment)||0)):0;
  const fmt2=v=>parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

  const handleDownloadExcel=()=>{
    const headers=['Sr No','Name','Account Number','Bank Name','Branch','IFSC Code','Net Payment'];
    const csvRows=[headers.join(','),...bankRows.map((r,i)=>[i+1,`"${r.name}"`,r.accountNumber||'',`"${r.bankName||''}"`,`"${r.branch||''}"`,r.ifscCode||'',parseFloat(r.payment||0).toFixed(2)].join(','))];
    csvRows.push('');csvRows.push(`"Lowest Amount:",${lowestAmt}`);csvRows.push(`"Highest Amount:",${highestAmt}`);
    csvRows.push(`"Total Number of Payments:",${bankRows.length}`);csvRows.push(`"Total Amount:",${fmt2(totalPay)}`);
    const blob=new Blob(['\uFEFF'+csvRows.join('\n')],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob);const link=document.createElement('a');
    link.href=url;link.download=`BankPayment_${fromDate}_${toDate}.csv`;
    document.body.appendChild(link);link.click();document.body.removeChild(link);URL.revokeObjectURL(url);
  };
  const handlePrint=()=>{
    const html=`<html><head><style>
      body{font-family:Arial,sans-serif;font-size:10px;margin:15px}h3{margin:0;font-size:12px}.sub{font-size:9px;color:#555}
      .title{color:#1a4fa0;font-weight:bold;font-size:11px;text-align:right}
      .stats{display:grid;grid-template-columns:1fr 1fr;gap:4px;margin:4px 0 8px;font-size:9px}
      table{width:100%;border-collapse:collapse}th{background:#e8f0fe;padding:4px 6px;border:1px solid #bbb;font-size:9px}
      td{padding:3px 6px;border:1px solid #ddd;font-size:9px}.r{text-align:right}
    </style></head><body>
      <div style="display:flex;justify-content:space-between">
        <div><h3>${user?.centerName||''}</h3><div class="sub">${user?.centerCode||''}</div></div>
        <div class="title">NEFT/RTGS Transfer File<br/><span style="font-weight:normal;font-size:9px">${fromDate} To ${toDate}</span></div>
      </div>
      <div class="stats"><div>Lowest: ${lowestAmt}</div><div>Count: ${bankRows.length}</div><div>Highest: ${highestAmt}</div><div>Total: ${totalPay}</div></div>
      <table><thead><tr><th>Sr</th><th>Name</th><th>Account Number</th><th>Bank</th><th>Branch</th><th>IFSC</th><th class="r">Payment</th></tr></thead><tbody>
        ${bankRows.map((r,i)=>`<tr><td>${i+1}</td><td>${r.name}</td><td>${r.accountNumber||''}</td><td>${r.bankName||''}</td><td>${r.branch||''}</td><td>${r.ifscCode||''}</td><td class="r">${fmt2(r.payment)}</td></tr>`).join('')}
      </tbody></table>
    </body></html>`;
    const w=window.open('','_blank','width=900,height=600');w.document.write(html);w.document.close();w.focus();setTimeout(()=>w.print(),400);
  };
  const thS=(align)=>({fontSize:'0.8rem',fontWeight:700,color:'#374151',padding:'9px 14px',borderBottom:'2px solid #e5e7eb',background:'#fff',whiteSpace:'nowrap',textAlign:align||'left'});
  const tdS=(align)=>({fontSize:'0.82rem',padding:'8px 14px',borderBottom:'1px solid #f3f4f6',whiteSpace:'nowrap',textAlign:align||'left'});

  return (
    <Layout title="Bank Payment File">
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
        <IconButton size="small" onClick={onBack} sx={{ color:theme.primary }}><ArrowBack/></IconButton>
        <Typography sx={{ fontWeight:700, color:theme.primary }}>Bank Payment File</Typography>
      </Box>
      <Box sx={{ display:'flex', alignItems:'center', gap:3, mb:2, flexWrap:'wrap' }}>
        <Typography sx={{ fontSize:'0.85rem' }}><b>From Date :</b> {fromDate.split('-').reverse().join('/')}</Typography>
        <Typography sx={{ fontSize:'0.85rem' }}><b>To Date :</b> {toDate.split('-').reverse().join('/')}</Typography>
        <Box sx={{ flex:1 }}/>
        <Button variant="contained" onClick={handlePrint} startIcon={<Print/>}
          sx={{ borderRadius:2, textTransform:'none', fontWeight:700, background:`linear-gradient(135deg,${theme.primary},${theme.hover||theme.primary})` }}>Preview</Button>
        <Button variant="contained" onClick={handleDownloadExcel} startIcon={<Download/>}
          sx={{ borderRadius:2, textTransform:'none', fontWeight:700, background:'#16a34a', '&:hover':{ background:'#15803d' } }}>Download Excel</Button>
      </Box>
      <Box sx={{ display:'flex', gap:4, mb:2, flexWrap:'wrap' }}>
        <Typography sx={{ fontSize:'0.82rem' }}>Lowest Amount: <b>{lowestAmt}</b></Typography>
        <Typography sx={{ fontSize:'0.82rem' }}>Highest Amount: <b>{highestAmt}</b></Typography>
        <Box sx={{ flex:1 }}/>
        <Typography sx={{ fontSize:'0.82rem' }}>Total Payments: <b>{bankRows.length}</b></Typography>
        <Typography sx={{ fontSize:'0.82rem' }}>Total Amount: <b>{totalPay}</b></Typography>
      </Box>
      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        <Box sx={{ display:'flex', justifyContent:'flex-end', px:2, py:1, borderBottom:`1px solid ${theme.border||'#e5e7eb'}`, background:theme.light }}>
          <TextField size="small" placeholder="Search" value={search} onChange={e=>setSearch(e.target.value)}
            sx={{ width:200, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              <th style={{ ...thS(), width:60 }}>Sr No</th><th style={thS()}>Name</th>
              <th style={thS()}>Account Number</th><th style={thS()}>Bank Name</th>
              <th style={thS()}>Branch</th><th style={thS()}>IFSC Code</th>
              <th style={thS('right')}>Net Payment</th>
            </tr></thead>
            <tbody>
              {bankRows.length===0
                ? <tr><td colSpan={7} style={{ textAlign:'center', padding:'48px', color:'#9ca3af', fontSize:'0.85rem' }}>No Records Found.</td></tr>
                : bankRows.map((row,idx)=>(
                    <tr key={idx} style={{ background:idx%2===0?'#fff':'#fafafa' }}>
                      <td style={tdS()}>{idx+1}</td><td style={tdS()}>{row.name}</td>
                      <td style={tdS()}>{row.accountNumber}</td><td style={tdS()}>{row.bankName||'—'}</td>
                      <td style={tdS()}>{row.branch||'—'}</td><td style={tdS()}>{row.ifscCode||'—'}</td>
                      <td style={tdS('right')}>{fmt2(row.payment)}</td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </Box>
        <Box sx={{ px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa', textAlign:'center', fontSize:'0.78rem', color:'#6b7280' }}>
          Showing 1 to {bankRows.length} of {bankRows.length} entries
        </Box>
      </Card>
    </Layout>
  );
}